import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import React from 'react';

import { Meta } from '../components/Meta';
import SliceZone from '../components/SliceZone';
import { Schemas } from '../seo/Schemas';

export const Head = ({ data, pageContext, location: { pathname } }) => {
  const { seo_title, seo_description, no_index, manual_href_langs } =
    data.prismicPage.data;

  return (
    <>
      <Meta
        title={seo_title}
        pageTitle={seo_title}
        description={seo_description}
        url={`${pageContext.siteURL}/${pathname}`}
        pageContext={pageContext}
        noIndex={no_index}
        manualHrefLangs={manual_href_langs}
      />
      <Schemas data={data} pageContext={pageContext} />
    </>
  );
};

function Page({ data, pageContext, location }) {
  if (!data.prismicPage) return <></>;

  const { body, slug } = data.prismicPage.data;

  return (
    <SliceZone
      slices={body}
      pageContext={pageContext}
      slug={slug}
      location={location}
    />
  );
}

export default withPrismicPreview(Page);

export const query = graphql`
  query ($slug: String!, $siteTag: String!, $siteLang: String!) {
    prismicPage(
      data: { slug: { eq: $slug }, locale: { eq: $siteLang } }
      tags: { eq: $siteTag }
    ) {
      _previewable
      data {
        slug
        locale
        seo_title
        seo_description
        no_index
        custom_schema {
          text
        }
        manual_href_langs {
          href
          hreflang
        }
        body {
          ... on PrismicPageDataBodyBlockCarousel {
            id
            primary {
              button {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
              section_description_1 {
                richText
                html
                text
              }
              section_description_2 {
                html
                richText
                text
              }
              section_description_3 {
                html
                richText
                text
              }
              section_heading_1 {
                html
                richText
                text
              }
              section_description_4 {
                html
                richText
                text
              }
              section_heading_3 {
                html
                richText
                text
              }
              section_heading_2 {
                html
                richText
                text
              }
              section_heading_4 {
                richText
                text
                html
              }
            }
            slice_label
            slice_type
            items {
              carousel_text {
                html
                richText
                text
              }
              image {
                alt
                url
                gatsbyImageData(width: 400)
              }
            }
          }
          ... on PrismicPageDataBodyBlockCompare {
            id
            slice_label
            slice_type
            primary {
              column_heading_one {
                html
                richText
                text
              }
              color
              column_heading_two {
                html
                richText
                text
              }
              compare_heading
              contact_button
              eyebrow {
                html
                richText
                text
              }
              footer {
                richText
              }
              partial_compare {
                id
                uid
              }
              partial_compare_2 {
                id
                uid
              }
              partial_comparison_table {
                id
                uid
              }
              slider_one_button
              slider_one_description
              slider_two_button
              slider_two_description
            }
          }
          ... on PrismicPageDataBodyBlockCompareOverview {
            id
            slice_label
            slice_type
            primary {
              heading {
                text
              }
              compare_heading
              compare_option_1
              compare_option_2
              stat_1_heading
              stat_1_description
              stat_2_heading
              stat_2_description
              stat_3_heading
              stat_3_description
              stat_4_heading
              stat_4_description
              stat_5_heading
              stat_5_description
              stat_6_heading
              stat_6_description
              stat_7_heading
              stat_7_description
              stat_8_heading
              stat_8_description
            }
          }
          ... on PrismicPageDataBodyBlockCompareTable {
            id
            slice_label
            slice_type
            primary {
              eyebrow {
                text
              }
              content {
                text
              }
              heading {
                text
                html
                richText
              }
              button_url
              button_text {
                text
              }
              column_heading_1
              column_heading_2
            }
            items {
              column_1
              column_2
              column_2_suffix
            }
          }
          ... on PrismicPageDataBodyBlockContact {
            id
            primary {
              background_image {
                url
                alt
                gatsbyImageData
              }
              button {
                html
                richText
                text
              }
              color
              content {
                html
                richText
                text
              }
              title_end {
                html
                richText
                text
              }
              title_middle {
                html
                richText
                text
              }
              title_start {
                html
                richText
                text
              }
              form_title {
                richText
              }
            }
            slice_label
            slice_type
          }
          ... on PrismicPageDataBodyBlockFacilityDiagram {
            id
            slice_label
            slice_type
            primary {
              section_id
              explore_text
              heading {
                richText
                text
              }
              content {
                richText
                text
              }
              video {
                link_type
                url
                target
              }
            }
            items {
              heading {
                text
              }
              content {
                richText
                text
              }
              image1 {
                alt
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockFacilityMap {
            id
            slice_label
            slice_type
            primary {
              latitude {
                text
              }
              longitude {
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockFooterCta {
            id
            slice_label
            slice_type
            primary {
              color
              button {
                html
                richText
                text
              }
              button_typeform_id
              title_end {
                html
                richText
                text
              }
              title_middle {
                html
                richText
                text
              }
              title_start {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              background {
                url
                alt
                gatsbyImageData
              }
              button_type
              button_link {
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockIconList {
            id
            slice_type
            slice_label
            primary {
              slice_anchor
              button {
                html
                richText
                text
              }
              title_start {
                html
                richText
                text
              }
              title_middle {
                html
                richText
                text
              }
              title_end {
                html
                richText
                text
              }
              eyebrow {
                html
                richText
                text
              }
            }
            items {
              description {
                html
                richText
                text
              }
              icon {
                alt
                url
              }
              heading {
                html
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockHeroPhotoLeft {
            id
            slice_label
            slice_type
            primary {
              heading_size
              button {
                html
                richText
                text
              }
              button_link {
                url
              }
              button_typeform_id
              video {
                url
              }
              video_button_text
              description {
                html
                richText
                text
              }
              heading_end {
                html
                richText
                text
              }
              heading_start {
                html
                richText
                text
              }
              background {
                alt
                url
                gatsbyImageData
              }
            }
          }
          ... on PrismicPageDataBodyBlockSixUp {
            id
            slice_label
            slice_type
            primary {
              slice_anchor
              color
              background {
                url
                alt
              }
              background_side
              button {
                richText
                text
              }
              button_link {
                url
              }
              eyebrow {
                richText
                text
              }
              description {
                richText
                text
              }
              heading_start {
                richText
                text
              }
              heading_end {
                richText
                text
              }
              section_description_one {
                richText
                text
              }
              section_description_two {
                richText
                text
              }
              section_description_three {
                richText
                text
              }
              section_description_four {
                richText
                text
              }
              section_description_five {
                richText
                text
              }
              section_description_six {
                richText
                text
              }
              section_heading_one {
                richText
                text
              }
              section_heading_two {
                richText
                text
              }
              section_heading_three {
                richText
                text
              }
              section_heading_four {
                richText
                text
              }
              section_heading_five {
                richText
                text
              }
              section_heading_six {
                richText
                text
              }
              section_icon_one {
                url
                alt
                gatsbyImageData
              }
              section_icon_two {
                url
                alt
                gatsbyImageData
              }
              section_icon_three {
                url
                alt
                gatsbyImageData
              }
              section_icon_four {
                url
                alt
                gatsbyImageData
              }
              section_icon_five {
                url
                alt
                gatsbyImageData
              }
              section_icon_six {
                url
                alt
                gatsbyImageData
              }
              disclaimer
              show_numbers
            }
          }
          ... on PrismicPageDataBodyBlockTechnology {
            id
            items {
              logo {
                url
                alt
                gatsbyImageData
              }
            }
            primary {
              button {
                html
                richText
                text
              }
              button_url
              eyebrow1 {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              heading_middle {
                html
                richText
                text
              }
              heading_end {
                html
                richText
                text
              }
              heading_start {
                html
                richText
                text
              }
              image1 {
                url
                alt
                gatsbyImageData
              }
            }
            slice_label
            slice_type
          }
          ... on PrismicPageDataBodyBlockFaqs {
            id
            primary {
              divider
              eyebrow1 {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
            }
            items {
              use_faq_schema
              answer {
                html
                richText
                text
              }
              question {
                html
                richText
                text
              }
            }
            slice_label
            slice_type
          }
          ... on PrismicPageDataBodyBlockLinks {
            id
            slice_type
            slice_label
            primary {
              heading {
                html
                richText
                text
              }
              link_one_copy {
                html
                richText
                text
              }
              link_one_slug
              link_three_copy {
                html
                richText
                text
              }
              link_three_slug
              link_two_slug
              link_two_copy {
                html
                richText
                text
              }
              background {
                url
                alt
                gatsbyImageData
              }
            }
          }
          ... on PrismicPageDataBodyBlockHeroVideo {
            id
            primary {
              button {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
              play_button {
                html
                richText
                text
              }
              video {
                url
              }
              background_video {
                url
              }
              background_video_poster {
                url
              }
            }
            slice_label
            slice_type
          }
          ... on PrismicPageDataBodyBlockHeroGrowth {
            id
            primary {
              button {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
              play_button {
                html
                richText
                text
              }
              video {
                url
              }
              background_video {
                url
              }
            }
            slice_label
            slice_type
          }
          ... on PrismicPageDataBodyBlockCustomersBlog {
            id
            primary {
              button {
                html
                richText
                text
              }
              eyebrow {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
            }
            slice_label
            slice_type
          }
          ... on PrismicPageDataBodyBlockTestimonialsVideo {
            id
            slice_label
            slice_type
            primary {
              description {
                html
                richText
                text
              }
              eyebrow {
                html
                richText
                text
              }
              heading {
                html
                text
                richText
              }
              portrait_one {
                alt
                url
                gatsbyImageData(width: 600)
              }
              portrait_two {
                alt
                url
                gatsbyImageData(width: 300)
              }
              portrait_three {
                alt
                url
                gatsbyImageData(width: 300)
              }
              portrait_four {
                alt
                url
                gatsbyImageData(width: 300)
              }
              portrait_five {
                alt
                url
                gatsbyImageData(width: 300)
              }
              testimonial_entity_five {
                html
                richText
                text
              }
              testimonial_entity_five_description {
                html
                richText
                text
              }
              testimonial_entity_four_description {
                richText
                html
                text
              }
              testimonial_entity_four {
                html
                richText
                text
              }
              testimonial_entity_one_description {
                html
                richText
                text
              }
              testimonial_entity_one {
                html
                richText
                text
              }
              testimonial_entity_three {
                html
                richText
                text
              }
              testimonial_entity_three_description {
                html
                richText
                text
              }
              testimonial_entity_two {
                html
                richText
                text
              }
              testimonial_entity_two_description {
                html
                richText
                text
              }
              video_five {
                url
              }
              video_four {
                url
              }
              video_three {
                url
              }
              video_one {
                url
              }
              video_two {
                url
              }
              button {
                text
                richText
                html
              }
              color
            }
          }
          ... on PrismicPageDataBodyBlockSpecs {
            id
            slice_type
            items {
              category {
                richText
                text
                html
              }
              category_image {
                url
                alt
              }
            }
            primary {
              comparison_image {
                url
                alt
              }
              button {
                html
                richText
                text
              }
              description {
                html
                text
                richText
              }
              eyebrow {
                html
                richText
                text
              }
              kitchen_spec_four {
                url
                id
              }
              kitchen_spec_one {
                url
                id
              }
              kitchen_spec_three {
                id
                url
              }
              kitchen_spec_two {
                id
                url
                uid
                type
              }
              title {
                html
                richText
                text
              }
              description_two {
                html
                richText
                text
              }
              title_two {
                richText
                html
                text
              }
              legend_list_item_selected {
                html
                text
                richText
              }
              legend_list_item_unselected {
                html
                richText
                text
              }
            }
            slice_label
          }
          ... on PrismicPageDataBodyBlockComparisonImage {
            id
            slice_type
            slice_label
            items {
              category {
                richText
              }
              category_image {
                url
                alt
              }
            }
            primary {
              comparison_image {
                url
                alt
              }
              caption {
                richText
              }
            }
          }
          ... on PrismicPageDataBodyBlockTestimonialsText {
            id
            slice_label
            slice_type
            primary {
              eyebrow {
                richText
                html
                text
              }
              heading {
                html
                text
                richText
              }
              color
              background {
                alt
                url
              }
            }
            items {
              owner {
                html
                text
                richText
              }
              restaurant_name {
                html
                richText
                text
              }
              testimonial_text {
                html
                text
                richText
              }
            }
          }
          ... on PrismicPageDataBodyBlockFeatures {
            id
            slice_label
            slice_type
            primary {
              description {
                html
                richText
                text
              }
              eyebrow {
                html
                richText
                text
              }
              features {
                id
              }
              specs {
                id
              }
              title {
                richText
                html
                text
              }
              cta_button_text {
                text
              }
              cta_button_link {
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockFourUp {
            id
            slice_type
            slice_label
            primary {
              button_one {
                html
                richText
                text
              }
              background {
                url
                alt
              }
              button_two {
                html
                richText
                text
              }
              color
              content_four {
                html
                richText
                text
              }
              content_one {
                html
                richText
                text
              }
              content_three {
                html
                richText
                text
              }
              content_two {
                richText
                html
                text
              }
              description {
                html
                richText
                text
              }
              eyebrow {
                html
                text
                richText
              }
              title {
                html
                richText
                text
              }
              title_four {
                html
                richText
                text
              }
              title_one {
                html
                richText
                text
              }
              title_three {
                html
                richText
                text
              }
              title_two {
                html
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockFourUpText {
            id
            slice_type
            slice_label
            primary {
              slice_anchor
              color
              eyebrow {
                richText
                text
              }
              heading {
                richText
                text
              }
            }
            items {
              icon {
                alt
                url
              }
              heading {
                richText
                text
              }
              content {
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockHeroText {
            id
            slice_label
            slice_type
            primary {
              optional_background_element {
                url
                alt
              }
              eyebrow {
                html
                richText
                text
              }
              button {
                html
                richText
                text
              }
              button_two {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
              button_type
              button_link {
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockThreeUp {
            id
            slice_label
            slice_type
            primary {
              content_one {
                html
                richText
                text
              }
              content_three {
                html
                richText
                text
              }
              content_two {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
              image {
                url
              }
              title_one {
                html
                richText
                text
              }
              title_three {
                html
                richText
                text
              }
              title_two {
                richText
                text
                html
              }
            }
          }
          ... on PrismicPageDataBodyBlockHeroForm {
            id
            slice_label
            slice_type
            primary {
              button_doc {
                document {
                  ...ButtonDocData
                }
              }
              video {
                url
              }
              video_button_text
              description {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
              button {
                html
                richText
                text
              }
              cta_button_text {
                text
              }
              cta_button_link {
                url
              }
              background {
                url
              }
              color
              form_variant
            }
            items {
              carousel_image {
                alt
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockHeroDownload {
            id
            primary {
              description {
                html
                richText
                text
              }
              heading {
                html
                richText
                text
              }
              background {
                alt
                url
              }
              button {
                richText
              }
              file {
                url
              }
              color
              utm_campaign
            }
            slice_label
            slice_type
          }
          ... on PrismicPageDataBodyBlockHeroPhotoRight {
            id
            slice_type
            slice_label
            primary {
              heading_start {
                html
                richText
                text
              }
              heading_end {
                html
                richText
                text
              }
              description_size
              description {
                html
                richText
                text
              }
              button {
                html
                richText
                text
              }
              button_link {
                url
              }
              background {
                alt
                url
              }
              video {
                url
              }
              video_button_text
            }
          }
          ... on PrismicPageDataBodyBlockTeamSlice {
            id
            slice_type
            slice_label
            primary {
              color
              description {
                html
                richText
                text
              }
              eyebrow {
                html
                richText
                text
              }
              heading_one {
                html
                richText
                text
              }
              image_one {
                alt
                url
              }
              image_three {
                alt
                url
              }
              image_two {
                alt
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockBlogs {
            id
            slice_label
            slice_type
            primary {
              empty_field
            }
          }
          ... on PrismicPageDataBodyBlockPress {
            id
            slice_label
            slice_type
            primary {
              featured_press {
                document {
                  ... on PrismicPage {
                    id
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyBlockResources {
            id
            slice_label
            slice_type
            primary {
              empty_field
            }
          }
          ... on PrismicPageDataBodyBlockOverview {
            id
            primary {
              color
              heading {
                richText
                richText
                html
              }
            }
            slice_label
            slice_type
            items {
              stat_heading
              stat_heading_detail
              stat_subheading
              description {
                html
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockValueProps {
            id
            slice_label
            slice_type
            primary {
              slice_anchor
              color
              heading {
                richText
                text
              }
            }
            items {
              icon {
                url
                alt
              }
              heading {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockEditorial {
            id
            slice_label
            slice_type
            primary {
              heading {
                html
                richText
                text
              }
              share_url
              image {
                url
                alt
              }
              description {
                html
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockPartners {
            id
            slice_label
            slice_type
            primary {
              heading {
                html
                richText
                text
              }
            }
            items {
              image {
                url
                alt
              }
            }
          }
          ... on PrismicPageDataBodyBlockOtherLocations {
            id
            slice_label
            slice_type
            primary {
              color
              button_1_text
              button_2_text
              nearby_image_1 {
                url
                alt
              }
              nearby_eyebrow_1
              nearby_heading_1 {
                html
                richText
                text
              }
              nearby_description_1 {
                html
                richText
                text
              }
              nearby_image_2 {
                url
                alt
              }
              nearby_eyebrow_2
              nearby_heading_2 {
                html
                richText
                text
              }
              nearby_description_2 {
                html
                richText
                text
              }
              nearby_image_3 {
                url
                alt
              }
              nearby_eyebrow_3
              nearby_heading_3 {
                html
                richText
                text
              }
              nearby_description_3 {
                html
                richText
                text
              }
              nearby_image_4 {
                url
                alt
              }
              nearby_eyebrow_4
              nearby_heading_4 {
                html
                richText
                text
              }
              nearby_description_4 {
                html
                richText
                text
              }
              most_searched_image_1 {
                url
                alt
              }
              most_searched_eyebrow_1
              most_searched_heading_1 {
                html
                richText
                text
              }
              most_searched_description_1 {
                html
                richText
                text
              }
              most_searched_image_2 {
                url
                alt
              }
              most_searched_eyebrow_2
              most_searched_heading_2 {
                html
                richText
                text
              }
              most_searched_description_2 {
                html
                richText
                text
              }
              most_searched_image_3 {
                url
                alt
              }
              most_searched_eyebrow_3
              most_searched_heading_3 {
                html
                richText
                text
              }
              most_searched_description_3 {
                html
                richText
                text
              }
              most_searched_image_4 {
                url
                alt
              }
              most_searched_eyebrow_4
              most_searched_heading_4 {
                html
                richText
                text
              }
              most_searched_description_4 {
                html
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockNearbyCityLocations {
            id
            slice_label
            slice_type
            primary {
              title {
                text
              }
              description {
                text
              }
              see_more_title {
                text
              }
              see_more_description {
                text
              }
              latitude {
                text
              }
              longitude {
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockLocationsPromo {
            id
            slice_label
            slice_type
            primary {
              color
              heading {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              heading_2 {
                html
                richText
                text
              }
              image_1 {
                alt
                url
              }
              image_2 {
                alt
                url
              }
              image_3 {
                alt
                url
              }
              image_4 {
                alt
                url
              }
              image_5 {
                alt
                url
              }
              image_6 {
                alt
                url
              }
              image_7 {
                alt
                url
              }
            }
            items {
              location_name
              latitude
              longitude
            }
          }
          ... on PrismicPageDataBodyBlockKitchenTypes {
            id
            slice_type
            slice_label
            primary {
              heading {
                richText
              }
            }
            items {
              image {
                url
                alt
              }
              cuisine_image_1 {
                url
                alt
              }
              cuisine_image_2 {
                url
                alt
              }
              cuisine_image_3 {
                url
                alt
              }
              heading {
                richText
              }
              subheading
              content {
                richText
              }
              image_title
              cuisine_1
              cuisine_2
              cuisine_3
            }
          }
          ... on PrismicPageDataBodyBlockStrategicLocations {
            id
            slice_label
            slice_type
            primary {
              heading {
                richText
                html
              }
              description_size
              description {
                richText
                html
              }
              cta
            }
          }
          ... on PrismicPageDataBodyBlockLogoStripe {
            id
            slice_label
            slice_type
            primary {
              heading {
                richText
                html
              }
              size
            }
            items {
              image {
                url
                alt
              }
            }
          }
          ... on PrismicPageDataBodyBlockCtaAlt {
            id
            slice_label
            slice_type
            primary {
              color
              form_background {
                alt
                url
              }
              form_title {
                html
                richText
                text
              }
              form_title_two {
                html
                richText
                text
              }
              social_media_platform {
                html
                richText
                text
              }
              social_media_url
            }
          }
          ... on PrismicPageDataBodyBlockCaseStudyHero {
            id
            slice_label
            slice_type
            primary {
              image_position
              heading {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              image {
                url
                alt
              }
              video_title
              video_description
              link {
                link_type
                url
                target
              }
            }
          }
          ... on PrismicPageDataBodyBlockCaseStudyContent {
            id
            slice_label
            slice_type
            primary {
              heading {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
            }
          }
          ... on PrismicPageDataBodyBlockFindYourGhostKitchen {
            id
            slice_label
            slice_type
            primary {
              button_text {
                text
              }
              typeform_button
              title {
                richText
              }
              paragraph {
                richText
              }
              search_placeholder
              facility
              facilities
              delivery
              takeout
            }
            items {
              title {
                richText
              }
              number_of_facilities
              region
              link {
                url
              }
              item_image {
                gatsbyImageData(width: 700)
              }
            }
          }
          ... on PrismicPageDataBodyBlockCaseStudyVideos {
            id
            slice_label
            slice_type
            items {
              image {
                alt
                url
              }
              heading {
                html
                richText
                text
              }
              description {
                html
                richText
                text
              }
              url
            }
          }
          ... on PrismicPageDataBodyBlockHeroTypeform {
            id
            slice_label
            slice_type
            primary {
              description {
                richText
              }
              heading {
                richText
              }
              button {
                richText
              }
              background {
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockSixUpAlt {
            id
            slice_label
            slice_type
            primary {
              eyebrow {
                richText
              }
              heading {
                richText
              }
              content {
                richText
              }
              button_1_text
              button_1_url
              button_2_text
              button_2_url
            }
            items {
              icon {
                url
                alt
              }
              heading {
                richText
              }
              content {
                richText
              }
              image_title
            }
          }
          ... on PrismicPageDataBodyBlockTestimonialsSliderAlt {
            slice_type
            primary {
              eyebrow {
                richText
              }
              heading {
                richText
              }
            }
            items {
              content {
                richText
              }
              cite_1
              cite_2
              cite_3
            }
          }
          ... on PrismicPageDataBodyBlockVideo {
            id
            slice_type
            primary {
              eyebrow {
                richText
              }
              heading {
                richText
              }
              content {
                richText
              }
              box_size
              image {
                url
                alt
              }
              image_title
              button_text {
                richText
              }
              video {
                url
              }
              background_video {
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockCompareAlt {
            id
            slice_label
            slice_type
            primary {
              heading {
                richText
              }
              eyebrow_1
              eyebrow_2
            }
            items {
              heading {
                richText
              }
              content {
                richText
              }
              value_1
              value_1_description
              value_2
              value_2_description
            }
          }
          ... on PrismicPageDataBodyBlockHeroTextAlt {
            id
            slice_label
            slice_type
            primary {
              eyebrow {
                richText
              }
              button {
                richText
              }
              button_link {
                link_type
                url
                target
              }
              description {
                richText
              }
              heading {
                richText
              }
            }
          }
          ... on PrismicPageDataBodyBlockCtaButton {
            id
            slice_label
            slice_type
            primary {
              color
              spacing_top
              spacing_bottom
              cta_text
              alignment
            }
          }
          ... on PrismicPageDataBodyBlockPressContent {
            slice_type
            primary {
              heading {
                richText
              }
              logo_1 {
                alt
                url
              }
              logo_2 {
                alt
                url
              }
              logo_3 {
                alt
                url
              }
              date_published
            }
            items {
              content_image {
                alt
                url
              }
              content {
                richText
              }
            }
          }
          ... on PrismicPageDataBodyBlockLegalText {
            slice_type
            primary {
              heading {
                richText
              }
              content {
                richText
              }
              raw_html {
                richText
              }
            }
          }

          ... on PrismicPageDataBodyBlockKitchenLayout {
            id
            slice_label
            slice_type
            primary {
              button_1_text
              button_1_url
              button_2_text
              button_2_url
              content {
                richText
              }
              eyebrow {
                richText
              }
              heading {
                richText
              }
              image {
                url
                alt
              }
              image_title
            }
            items {
              content {
                richText
              }
              heading {
                richText
              }
              number
            }
          }

          ... on PrismicPageDataBodyBlockPhotoLeft {
            id
            slice_label
            slice_type
            primary {
              slice_anchor
              button_1_url
              button_1_text
              eyebrow {
                richText
              }
              heading {
                richText
              }
              image {
                alt
                url
              }
              image_title
            }
            items {
              content {
                richText
              }
              heading {
                richText
              }
              number
            }
          }

          ... on PrismicPageDataBodyBlockPhotoFullBleed {
            id
            slice_label
            slice_type
            primary {
              image {
                url
              }
              image_title
            }
          }

          ... on PrismicPageDataBodyBlockVirtualBrands {
            id
            slice_label
            slice_type
            primary {
              button_text {
                richText
              }
              button_url
              content {
                richText
              }
              eyebrow {
                richText
              }
              image {
                alt
                url
              }
              heading {
                richText
              }
              image_title
            }
          }

          ... on PrismicPageDataBodyBlockPdf {
            id
            slice_label
            slice_type
            primary {
              heading {
                richText
              }
              media {
                url
              }
            }
          }

          ... on PrismicPageDataBodyBlockEntitiesLocations {
            slice_type
            items {
              eyebrow {
                text
              }
              heading {
                text
              }
              facility_name
              number_of_kitchens
              operational_hours
              dine_in
              address
              latitude
              longitude
            }
          }
          ... on PrismicPageDataBodyBlockScrollable {
            slice_type
            primary {
              eyebrow {
                richText
              }
              headline {
                richText
              }
              variant
              group_1_label
              group_2_label
              group_3_label
            }
            items {
              group
              label
              link {
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlockDivider {
            slice_type
            primary {
              line_color
            }
          }
          ... on PrismicPageDataBodyBlockStatsBanner {
            slice_type
            primary {
              title {
                richText
              }
            }
            items {
              stat {
                richText
              }
              stat_superscript {
                richText
              }
              stat_label {
                richText
              }
            }
          }
          ... on PrismicPageDataBodyBlockPhotoContainer {
            slice_type
            primary {
              slice_image {
                url
                alt
                gatsbyImageData
              }
            }
          }
          ... on PrismicPageDataBodyBlockFourUpTeam {
            id
            slice_type
            primary {
              section_id
              heading {
                html
                richText
                text
              }
              text
            }
            items {
              image1 {
                alt
                url
              }
              name
              job_position
              description_text
            }
          }
          ... on PrismicPageDataBodyBlockTabbedPhotoModule {
            slice_type
            primary {
              section_id
              color
              stats_label {
                html
                richText
                text
              }
              stats_content {
                html
                richText
                text
              }
              photo_detail_cta
            }
            items {
              category {
                html
                richText
                text
              }
              category_image {
                alt
                url
              }
              photo_detail {
                alt
                url
              }
              stats_content {
                html
                richText
                text
              }
              spec_five_name
              spec_five_superscript
              spec_five_value
              spec_four_name
              spec_four_superscript
              spec_four_value
              spec_one_name
              spec_one_superscript
              spec_one_value
              spec_three_name
              spec_three_superscript
              spec_three_value
              spec_two_name
              spec_two_superscript
              spec_two_value
            }
          }
          ... on PrismicPageDataBodyBlockEntitiesForm {
            slice_type
          }
          ... on PrismicPageDataBodyBlockYoutubeEmbed {
            slice_type
            primary {
              embed {
                html
                title
              }
            }
          }
          ... on PrismicPageDataBodyBlock404 {
            slice_type
            primary {
              title {
                richText
              }
              description {
                richText
              }
            }
          }
          ... on PrismicPageDataBodyBlockEnterpriseKitchens {
            slice_type
            primary {
              eyebrow {
                richText
              }
            }
            items {
              kitchen_image {
                alt
                url
                gatsbyImageData
              }
              kitchen_name
            }
          }
          ... on PrismicPageDataBodyBlockRestaurantTech {
            slice_type
            primary {
              eyebrow {
                richText
              }
              restaurant_tech_heading
              restaurant_tech_content {
                richText
              }
            }
            items {
              tech_image {
                alt
                url
                gatsbyImageData
              }
              footage
              tech_title
              tech_subtitle
              stat_1
              stat_2
              stat_3
            }
          }
          ... on PrismicPageDataBodyBlockFacilityType {
            slice_type
            primary {
              facility_title {
                richText
              }
              facility_description {
                richText
              }
              facility_specs {
                alt
                url
                gatsbyImageData
              }
              facility_image {
                alt
                url
                gatsbyImageData
              }
              kitchen_features
              features_title
              features_sub_title
              column_1_title
              column_1_list {
                text
                richText
              }
              column_2_title
              column_list_2 {
                text
                richText
              }
              column_3_title
              column_list_3 {
                text
                richText
              }
              additional_text
              download_text
              share_text
            }
            items {
              kitchen_feature_image {
                alt
                url
                gatsbyImageData
              }
              kitchen_feature_title
              kitchen_feature_description
            }
          }
          ... on PrismicPageDataBodyBlockFacilityCompanies {
            slice_type
            primary {
              facility_companies_title {
                richText
              }
            }
            items {
              company_image {
                alt
                url
                gatsbyImageData
              }
              company_eyebrow
              company_title
              company_description
            }
          }
          # Add new Blocks (Slices) above this line
        }
      }
    }
  }
`;
